import ExpandMore from "@mui/icons-material/ExpandMore";
import Person from "@mui/icons-material/Person";
import PersonOutlined from "@mui/icons-material/PersonOutlined";
import PlaylistAdd from "@mui/icons-material/PlaylistAdd";
import WorkOutlined from "@mui/icons-material/WorkOutlined";
import { Box, Chip, Collapse, IconButton, Typography } from "@mui/material";
import { type FC, memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../../../components/InHouseTooltip/InHouseTooltip";
import { JumpingDots } from "../../../../../../components/UIToolkit/Animations/JumpingDots";
import { TagChip } from "../../../../../../components/UIToolkit/Badges/TagChip";
import { useChosenDepartment } from "../../../../../../contexts/ChosenDepartment";
import { hrisEmployeeName } from "../../../../../../utils/formatters";
import { type HrisEmployeeShiftStats, type HrisRawEmployeeRow, type Job, type JobNotEnabledEmployee, Position } from "../../../../../../utils/models";
import { AdditionalJobsDialog } from "./AdditionalJobsDialog";
import { EmployeeSyncStatusIcon } from "./EmployeeSyncStatusIcon";
import { JobPickerCell, type JobPickerChange } from "./JobPickerCell";
import { SyncStatusIcon } from "./SyncStatusIcon";
import { countSyncingEmployees } from "./syncStatus";
import type { RoleStringRow } from "./useRoleStringRows";

interface RoleStringRowItemProps {
	row: RoleStringRow;
	jobs: Job[];
	isPending: boolean;
	onJobChange: (roleString: string, change: JobPickerChange) => void;
	/** Fires with the row's new additional jobs (jobs[1:]) when the admin confirms the additional-jobs dialog (DAT-466). */
	onAdditionalJobsChange: (roleString: string, additionalJobIds: string[]) => void;
	/** Per-employee activity stats keyed by employeeId — drives the per-employee shift-count hover popup. */
	statsByEmployeeId: Map<string, HrisEmployeeShiftStats>;
	/**
	 * Employees whose primary job this department doesn't enable, keyed by employeeId (DAT-834) —
	 * drives the fourth per-employee status icon. Keyed by employeeId, not nurseId, because a raw HRIS
	 * row carries no nurse id.
	 */
	jobNotEnabledByEmployeeId: Map<string, JobNotEnabledEmployee>;
	/** Parent hint to open this row (e.g. search narrowed to a single dept and this row matched). Only opens — never auto-collapses. */
	autoExpand: boolean;
}

/**
 * Single role-string row inside an internal_department group. Owns its own collapse state
 * so toggling one row does not re-render its siblings — critical for perf at department
 * sizes (N rows × heavy SystemDropdown each).
 */
export const RoleStringRowItem: FC<RoleStringRowItemProps> = memo(
	({ row, jobs, isPending, onJobChange, onAdditionalJobsChange, statsByEmployeeId, jobNotEnabledByEmployeeId, autoExpand }) => {
		const { t } = useTranslation();
		const { chosenDepartment } = useChosenDepartment();
		const [expanded, setExpanded] = useState(false);
		const [additionalJobsDialogOpen, setAdditionalJobsDialogOpen] = useState(false);
		const handleToggle = useCallback(() => setExpanded((prev) => !prev), []);
		const handleChange = useCallback((change: JobPickerChange) => onJobChange(row.roleString, change), [onJobChange, row.roleString]);
		const handleAdditionalJobsConfirm = useCallback(
			(newAdditionalJobIds: string[]) => {
				setAdditionalJobsDialogOpen(false);
				onAdditionalJobsChange(row.roleString, newAdditionalJobIds);
			},
			[onAdditionalJobsChange, row.roleString],
		);

		// Open (never close) when the parent flags this row as a search match. Lets users still
		// collapse manually without the next keystroke fighting them.
		useEffect(() => {
			if (autoExpand) setExpanded(true);
		}, [autoExpand]);

		const isDontSync = row.position === Position.DontSync;
		const syncedCount = countSyncingEmployees(row, statsByEmployeeId);
		const totalCount = row.rawEmployees.length;
		const syncJob = row.jobs[0] ?? null;
		const additionalJobs = row.jobs.slice(1);
		// Additional jobs hang off a real mapping — orphan/DONT_SYNC rows have no job to attach to.
		const canEditAdditionalJobs = !row.isOrphan && !isDontSync && syncJob !== null;
		// Stable identity so the dialog's open-reseed effect doesn't refire on unrelated re-renders.
		const additionalJobIds = useMemo(() => row.jobs.slice(1).map((j) => j.id), [row.jobs]);

		return (
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					borderRadius: 1,
					// Orphan rows keep a full warning border; mapped rows drop it for a hover wash + hairline so long lists read as one list, not boxed cards.
					...(row.isOrphan
						? { border: "1px solid", borderColor: "warning.light" }
						: {
								border: "1px solid transparent",
								"@media (hover: hover) and (pointer: fine)": {
									"&:hover": { backgroundColor: "action.hover" },
								},
								transition: "background-color 150ms ease-out",
								"@media (prefers-reduced-motion: reduce)": { transition: "none" },
							}),
					px: 1,
					py: 0.5,
				}}
				data-testid={`hris-mapping-role-row-${row.roleString}`}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
						<IconButton size="small" onClick={handleToggle} aria-label="toggle">
							<ExpandMore sx={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
						</IconButton>
						<Person fontSize="small" sx={{ color: "text.secondary" }} />
					</Box>
					<Box sx={{ flex: 1 }}>
						<Typography sx={{ fontWeight: 500 }}>{row.roleString}</Typography>
						<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
							<Typography variant="caption" color="text.secondary">
								{t("hris.mapping.rawEmployees.employeeCount", { count: totalCount })}
							</Typography>
							<SyncStatusIcon
								syncedCount={syncedCount}
								totalCount={totalCount}
								dontSync={isDontSync}
								testId={`hris-mapping-role-sync-status-${row.roleString}`}
							/>
						</Box>
					</Box>
					{isPending ? (
						<Box sx={{ minWidth: 220, display: "flex", justifyContent: "center" }} data-testid={`hris-mapping-pending-${row.roleString}`}>
							<JumpingDots />
						</Box>
					) : (
						<>
							<JobPickerCell
								roleString={row.roleString}
								jobs={jobs}
								selectedJob={syncJob}
								isDontSync={isDontSync}
								isOrphan={row.isOrphan}
								currentRole={row.role}
								onChange={handleChange}
							/>
							{canEditAdditionalJobs && (
								<InHouseTooltip title={t("hris.mapping.additionalJobs.add")}>
									<IconButton
										size="small"
										onClick={() => setAdditionalJobsDialogOpen(true)}
										aria-label={t("hris.mapping.additionalJobs.add")}
										data-testid={`hris-mapping-additional-jobs-button-${row.roleString}`}
									>
										<PlaylistAdd fontSize="small" />
									</IconButton>
								</InHouseTooltip>
							)}
						</>
					)}
				</Box>
				{additionalJobs.length > 0 && (
					<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, pl: 5, pb: 0.5 }} data-testid={`hris-mapping-additional-jobs-${row.roleString}`}>
						{additionalJobs.map((job) => (
							<Chip
								key={job.id}
								size="small"
								icon={<WorkOutlined />}
								label={job.displayName ?? job.name}
								variant="outlined"
								clickable={canEditAdditionalJobs}
								onClick={canEditAdditionalJobs ? () => setAdditionalJobsDialogOpen(true) : undefined}
								data-testid={`hris-mapping-additional-job-chip-${row.roleString}-${job.name}`}
							/>
						))}
					</Box>
				)}
				<Collapse in={expanded} mountOnEnter unmountOnExit>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, pl: 5, py: 1 }}>
						{row.rawEmployees.map((emp: HrisRawEmployeeRow) => {
							const name = hrisEmployeeName(emp);
							const stats = statsByEmployeeId.get(emp.employeeId);
							const otherDeptName = emp.departmentId != null && emp.departmentId !== chosenDepartment.department_id ? emp.departmentDisplayName : null;
							return (
								<Box key={emp.employeeId} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
									<PersonOutlined fontSize="small" sx={{ color: "text.secondary" }} />
									<Typography variant="body2">{name}</Typography>
									{otherDeptName && (
										<TagChip
											label={otherDeptName}
											tooltip={t("hris.mapping.rawEmployees.syncsToOtherDept", { department: otherDeptName })}
											dataTestId={`hris-mapping-employee-syncs-to-${emp.employeeId}`}
											sx={{ maxWidth: 160 }}
										/>
									)}
									<Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
										<EmployeeSyncStatusIcon emp={emp} stats={stats} jobNotEnabled={jobNotEnabledByEmployeeId.get(emp.employeeId)} />
									</Box>
								</Box>
							);
						})}
					</Box>
				</Collapse>
				{canEditAdditionalJobs && syncJob && (
					<AdditionalJobsDialog
						open={additionalJobsDialogOpen}
						roleString={row.roleString}
						jobs={jobs}
						syncJobId={syncJob.id}
						initialSelectedIds={additionalJobIds}
						linkedEmployeeCount={totalCount}
						onConfirm={handleAdditionalJobsConfirm}
						onClose={() => setAdditionalJobsDialogOpen(false)}
					/>
				)}
			</Box>
		);
	},
);
